import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import {
  useFilterMembers,
  useDeleteMember,
  useUpdateMemberStatus,
  usePublishedMembersCount,
  useDraftMembersCount,
  useSearchMembers,
  useMembersCategoryCount
} from '@/lib/api/hooks/member';
import type { Member } from '@/lib/api/types';
import { showToast } from '@/lib/toast';
import MembersStats from '@/pages/about/leadership/MembersStats';
import { Loader } from '@/components/ui/Loader';
import { useQueryClient } from '@tanstack/react-query';
import { ActionDropdown, DeleteModal, PublishModal, UnpublishModal, type DropdownAction, Button, Pagination } from '@/components/ui';
import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay';
import FilterModule from '@/components/ui/FilterModule';
import Search from '@/components/ui/Search';
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams';
import { useMembersFilterSections } from '@/pages/about/leadership/FilterModule/FILTER_SECTIONS';
import { Paragraph } from '@/components/ui/Typography';

type TabType = 'PUBLISH' | 'DRAFT';
type CategoryTabType = 'PRESIDENTIAL_COUNCIL' | 'MEMBERS';

const MemberCard: React.FC<{
  member: Member;
  onEdit: (member: Member) => void;
  onDelete: (member: Member) => void;
  onPublish: (member: Member) => void;
  onUnpublish: (member: Member) => void;
  onClick: (member: Member) => void;
  getDropdownActions: (member: Member) => DropdownAction[];
}> = ({ member, onClick, getDropdownActions }) => {
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'PRESIDENT':
        return 'bg-wine-100 text-white';
      case 'VICE_PRESIDENT':
        return 'bg-wine-100 text-white';
      case 'DIRECTOR':
        return 'bg-imageBg-100 text-forestGreen-100';
      case 'SECRETARY':
        return 'bg-green-100 text-green-800';
      case 'MEMBER':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCategory = (category: string) => {
    if (category === 'VICE_PRESIDENT') {
      return 'Vice-President';
    }
    return category.charAt(0) + category.slice(1).toLowerCase().replace(/_/g, '-');
  };

  const formatUpdatedDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div
      className="group border border-gray-200 bg-white rounded-lg hover:shadow-md transition-shadow cursor-pointer  flex flex-col"
      onClick={() => onClick(member)}
    >
      <div className="relative md:h-48 xl:h-64 bg-gray-100 overflow-hidden rounded-t-lg">
        {member.imageUrl ? (
          <img
            src={member.imageUrl}
            alt={member.fullName}
            className="w-full h-full object-top object-cover border-b border-gray-200"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-200 aspect-video">
            <span className="text-4xl font-bold text-gray-400">
              {member.fullName.charAt(0)}
            </span>
          </div>
        )}
        <span className={`absolute top-2 left-2 px-2.5 py-1 text-xs font-medium rounded-md ${
          member.status === 'PUBLISH' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-50 text-blue-500'
        }`}>
          {member.status === 'PUBLISH' ? 'Published' : 'Draft'}
        </span>
        <div className="absolute bg-white top-2 right-2 z-20" onClick={(e) => e.stopPropagation()}>
          <ActionDropdown
            id={member.memberId}
            actions={getDropdownActions(member)}
          />
        </div>
      </div>
      <div className="p-4 relative">
          <div className="absolute top-2 right-2 z-20" onClick={(e) => e.stopPropagation()}>
          <ActionDropdown
            id={member.memberId}
            actions={getDropdownActions(member)}
          />
        </div>
        <h3 className="font-bold text-gray-900 text-base line-clamp-1 mb-2">
          {member.fullName}
        </h3>
        <span className={`inline-block px-2.5 py-1 text-xs font-medium rounded-md ${getCategoryColor(member.memberCategory)}`}>
          {formatCategory(member.memberCategory)}
        </span>
        {member.countryName && member.countryName !== 'N/A' && (
          <div className="flex items-center gap-1.5 mt-3 text-xs text-gray-600">
            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{member.countryName}</span>
          </div>
        )}
        {member.updatedAt && (
          <p className="text-xs text-gray-400 mt-2">Updated {formatUpdatedDate(member.updatedAt)}</p>
        )}
      </div>
    </div>
  );
};

const Members: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('PUBLISH');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [unpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const [selectedMemberForDelete, setSelectedMemberForDelete] = useState<Member | null>(null);
  const [selectedMemberForStatusUpdate, setSelectedMemberForStatusUpdate] = useState<Member | null>(null);
  const [activeCategoryTab, setActiveCategoryTab] = useState<CategoryTabType>('PRESIDENTIAL_COUNCIL');


  
  const FILTER_SECTIONS = useMembersFilterSections();
        // ── URL-driven search / filter state ──────────────────────────────────────
        const {
          mode,
          searchTerm,
          filterState,
          page,
          size,
          setSearch,
          setFilters,
          removeFilter,
          clearFilters,
          setPage,
          apiFilters,
          getActiveFilterCount
        } = useSearchFilterParams(FILTER_SECTIONS)
      
    
    
      // Search API (when searching)
      const { data: searchData, isLoading: isSearchLoading, isError: isSearchError, refetch: refetchSearch } = useSearchMembers(
        { page, size },
      searchTerm,
        mode === 'search' 
      )
    
      // Filter API (when filtering or default)
      const { data: filterData, isLoading: isFilterLoading, isError: isFilterError, refetch: refetchFilter } = useFilterMembers(
        { page, size },
        {status: activeTab, memberCategory: activeCategoryTab === 'PRESIDENTIAL_COUNCIL' ? null : 'MEMBER', ...apiFilters },
              mode === 'filter' || mode === 'default'
      )
    
      // Select appropriate data source: search takes priority
      const aggregateData = mode === 'search' ? searchData : filterData
      const isLoading = mode === 'search' ? isSearchLoading : isFilterLoading
      const isError = mode === 'search' ? isSearchError : isFilterError
    
      const totalPages = aggregateData?.data?.totalPage ?? 1
      const activeFilterCount = getActiveFilterCount()
      const refetchData = mode === 'search' ? refetchSearch : refetchFilter
      const emptyMessage = mode === 'search'
        ? 'No members found matching your search criteria.'
        : mode === "filter" ?  'No members found matching your filter criteria.'
        : 'No members found.'

        const filteredMembers = aggregateData?.data?.data || [];

  // Category filter for Presidential Council (President/Vice-President) vs Members
  const getCategoryFilter = (tab: CategoryTabType): string[] => {
    if (tab === 'PRESIDENTIAL_COUNCIL') {
      return ['PRESIDENT', 'VICE_PRESIDENT'];
    }
    return ['MEMBER'];
  };

  const { data: publishedCountData } = usePublishedMembersCount();
  const { data: draftCountData } = useDraftMembersCount();
  const { data: membersCategoryCountData, isFetching: isMembersCategoryCountFetching, isError: isMembersCategoryCountError, refetch: refetchMembersCategoryCount } = useMembersCategoryCount();

  const deleteMemberMutation = useDeleteMember();
  const updateStatusMutation = useUpdateMemberStatus();


  // TODO This will be removed when the filter is updated to an array
  // Filter by active category tab 
  const categoryFilteredMembers = filteredMembers
    .filter((member: Member) => {
      const categoryFilter = getCategoryFilter(activeCategoryTab);
      return categoryFilter.includes(member.memberCategory);
    })
    .sort((a: Member, b: Member) => {
      // Sort President before Vice-President in Presidential Council tab
      if (activeCategoryTab === 'PRESIDENTIAL_COUNCIL') {
        const order: Record<string, number> = { 'PRESIDENT': 1, 'VICE_PRESIDENT': 2 };
        return (order[a.memberCategory] || 99) - (order[b.memberCategory] || 99);
      }
      return 0;
    });

  const publishedCount = publishedCountData?.data || 0;
  const draftCount = draftCountData?.data || 0;
  const membersCategoryCount = membersCategoryCountData?.data || {};

  const handleAddMember = () => {
    navigate('/admin/members/add');
  };

  const handleViewMember = (member: Member) => {
    navigate(`/admin/members/${member.memberId}`);
  };

  const handleEditMember = (member: Member) => {
    navigate(`/admin/members/edit/${member.memberId}`, {
      state: { member }
    });
  };

  const handleDeleteMember = (member: Member) => {
    setSelectedMemberForDelete(member);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedMemberForDelete) return;

    const deleteData = {
      fullName: selectedMemberForDelete.fullName,
      countryId: selectedMemberForDelete.countryId,
      memberCategory: selectedMemberForDelete.memberCategory,
      imageUrl: selectedMemberForDelete.imageUrl,
      isBio: true,
      bio: selectedMemberForDelete.bio,
      socialMediaLinks: selectedMemberForDelete.socialMediaLinks,
      status: selectedMemberForDelete.status || 'DRAFT'
    };

    deleteMemberMutation.mutate(
      {
        memberId: selectedMemberForDelete.memberId,
        data: deleteData
      },
      {
        onSuccess: (response) => {
          if (response.success) {
            showToast.success('Member deleted successfully');
            setDeleteModalOpen(false);
            setSelectedMemberForDelete(null);
            queryClient.invalidateQueries({ queryKey: ['members'] });
          } else {
            showToast.error('Failed to delete member');
          }
        },
        onError: (error: Error) => {
          showToast.error('Failed to delete: ' + (error.message || 'Unknown error'));
        }
      }
    );
  };

  const handlePublishMember = (member: Member) => {
    setSelectedMemberForStatusUpdate(member);
    setPublishModalOpen(true);
  };

  const handleUnpublishMember = (member: Member) => {
    setSelectedMemberForStatusUpdate(member);
    setUnpublishModalOpen(true);
  };

  const confirmStatusUpdate = () => {
    if (!selectedMemberForStatusUpdate) return;

    const newStatus = selectedMemberForStatusUpdate.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';

    updateStatusMutation.mutate(
      {
        memberId: selectedMemberForStatusUpdate.memberId,
        data: { status: newStatus }
      },
      {
        onSuccess: () => {
          showToast.success(
            newStatus === 'PUBLISH'
              ? 'Member published successfully!'
              : 'Member unpublished successfully!'
          );
          setPublishModalOpen(false);
          setUnpublishModalOpen(false);
          setSelectedMemberForStatusUpdate(null);
          queryClient.invalidateQueries({ queryKey: ['members'] });
        },
        onError: (error: Error) => {
          showToast.error('Failed to update status: ' + (error.message || 'Unknown error'));
        }
      }
    );
  };

  const getDropdownActions = (member: Member): DropdownAction[] => {
    const actions: DropdownAction[] = [
      {
        label: 'View',
        onClick: (e) => {
          e.stopPropagation();
          handleViewMember(member);
        }
      },
      {
        label: 'Edit Member',
        onClick: (e) => {
          e.stopPropagation();
          handleEditMember(member);
        }
      },
      {
        label: member.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
        onClick: (e) => {
          e.stopPropagation();
          if (member.status === 'PUBLISH') {
            handleUnpublishMember(member);
          } else {
            handlePublishMember(member);
          }
        },
        disabled: updateStatusMutation.isPending
      }
    ];

    // Only show Delete for draft members
    if (member.status === 'DRAFT') {
      actions.push({
        label: 'Delete',
        onClick: (e) => {
          e.stopPropagation();
          handleDeleteMember(member);
        },
        variant: 'danger' as const,
        disabled: deleteMemberMutation.isPending
      });
    }

    return actions;
  };

  
  const isEmptyState = !isLoading && mode === "default" && filteredMembers.length === 0;


  return (
    <div className="flex flex-col gap-4 lg:gap-8">
      {/* Stats Section */}
      <MembersStats />
      
  
      {/* Tabs */}
      <div className="flex items-center justify-between pb-5">
        <div className="flex items-center gap-0">
          <button
            onClick={() => {
              setActiveTab('PUBLISH');
            }}
            className={`px-6 py-2.5 text-sm font-semibold transition-colors rounded-md ${activeTab === 'PUBLISH'
              ? 'bg-wine-100 text-white'
              : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            Published ({publishedCount})
          </button>
          <button
            onClick={() => {
              setActiveTab('DRAFT');
            }}
            className={`px-6 py-2.5 text-sm font-semibold transition-colors rounded-md ${activeTab === 'DRAFT'
              ? 'bg-wine-100 text-white'
              : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            Drafts ({draftCount})
          </button>
        </div>
        <button
          onClick={handleAddMember}
          className="bg-[#047857] hover:bg-[#059669] text-white font-semibold py-2.5 px-6 rounded-md text-sm"
        >
          Add Member
        </button>
      </div>
                                               {/* Search + Filter Controls */}
                <div className="flex flex-col gap-4 p-4 border border-gray-200 rounded-md bg-white shadow-sm">
                  <div className="flex items-center relative gap-4 justify-between flex-wrap">
                    <Search
                      value={searchTerm}
                      onSearch={setSearch}
                      placeholder="Search members..."
                    />
                     <FilterModule
                          sections={FILTER_SECTIONS}
                          activeFilters={filterState}
                          onApply={setFilters}
                          activeFilterCount={activeFilterCount}
                        />
                  </div>
      
                  {/* Active filter chips — only shown in filter mode */}
                  <FilterParamsDisplay
                    sections={FILTER_SECTIONS}
                    filterState={filterState}
                    onRemove={removeFilter}
                    onClearAll={clearFilters}
                  />
                </div>

      {/* Category Tabs */}
      {
        isMembersCategoryCountError ? (
          <div className="flex items-center justify-center py-4">
            <Paragraph className="">Error loading categories.</Paragraph>
            <Button
              onClick={() => {
                refetchMembersCategoryCount();
              }}
              className=""
            >
              Retry
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <button
              onClick={() => setActiveCategoryTab('PRESIDENTIAL_COUNCIL')}
              className={`pb-2 text-sm font-medium transition-colors border-b-2 ${activeCategoryTab === 'PRESIDENTIAL_COUNCIL'
            ? 'border-wine-100 text-wine-100'
            : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
        >
          Presidential Council {isMembersCategoryCountFetching ? <Loader size="xs" variant="wine" message="" /> : `(${membersCategoryCount[activeTab.toLowerCase() === 'publish' ? 'published' : 'draft']['president'] + membersCategoryCount[activeTab.toLowerCase() === 'publish' ? 'published' : 'draft']['vicePresident'] || 0})`}
        </button>
        <button
          onClick={() => setActiveCategoryTab('MEMBERS')}
          className={`pb-2 text-sm font-medium transition-colors border-b-2 ${activeCategoryTab === 'MEMBERS'
            ? 'border-wine-100 text-wine-100'
            : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
        >
          Members {isMembersCategoryCountFetching ? <Loader size="xs" variant="wine" message="" /> : `(${membersCategoryCount[activeTab.toLowerCase() === 'publish' ? 'published' : 'draft']['member'] || 0})`}
        </button>
      </div>
        )
      }

      {/* Members Grid */}
      <div>
        {isLoading ? (
          <Loader size="lg" variant="wine" message="Loading members..." />
        ) : isError ? (
                  <div className="p-8 flex items-center justify-center  flex-col gap-4 text-center">
                    <Paragraph className="">Error loading data.</Paragraph>
                    <Button
                      onClick={() => {
                        refetchData();
                      }}
                      className=""
                    >
                      Retry
                    </Button>
                  </div>
                )  : isEmptyState ? <div className="text-center py-20">
                <div className="inline-block bg-gray-100 p-4 rounded-full mb-4">
                  <img src="/No-post-icon.png" alt="No member" className="w-10 h-10 object-contain" />
                </div>
                <h3 className="font-semibold text-gray-800 text-lg">
                  {emptyMessage}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  { activeTab === 'PUBLISH' ? 'Upload your first member to get started' :  activeTab === 'DRAFT' ? 'No draft members yet' : 'No Data Found'}
                </p>
                  <Button variant="primary" className="mt-4" onClick={handleAddMember}>
                    Add Member
                  </Button>
              </div> : categoryFilteredMembers.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-block bg-gray-200/20 p-4 rounded-md">
         <img src="/No-post-icon.png" alt="No members" className="w-20 h-20 object-contain" />
            </div>
            <h3 className="mt-4 font-semibold text-gray-800 text-lg">No Members</h3>
            <p className="mt-1 text-sm text-gray-500">
              {emptyMessage}
            </p>
       
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
              {categoryFilteredMembers.map((member: Member) => (
                <MemberCard
                  key={member.memberId}
                  member={member}
                  onEdit={handleEditMember}
                  onDelete={handleDeleteMember}
                  onPublish={handlePublishMember}
                  onUnpublish={handleUnpublishMember}
                  onClick={handleViewMember}
                  getDropdownActions={getDropdownActions}
                />
              ))}
            </div>
<Pagination
                            currentPage={page}
                            totalPages={totalPages}
                            onPageChange={setPage}
                            className="p-4"
                          />
          </>
        )}
      </div>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Member"
        subtitle="Are you sure you want to delete this member?"
        itemName={selectedMemberForDelete?.fullName}
        warningText="This action cannot be undone. The member will be permanently deleted."
        isLoading={deleteMemberMutation.isPending}
      />

      {/* Publish Modal */}
      {selectedMemberForStatusUpdate && selectedMemberForStatusUpdate.status === 'DRAFT' && (
        <PublishModal
          isOpen={publishModalOpen}
          onClose={() => setPublishModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          title="Publish Member?"
          subtitle="Are you sure you want to publish this member?"
          itemName={selectedMemberForStatusUpdate.fullName}
          infoText="This member will be visible to the public once published."
          itemType="Member"
          isLoading={updateStatusMutation.isPending}
        />
      )}

      {/* Unpublish Modal */}
      {selectedMemberForStatusUpdate && selectedMemberForStatusUpdate.status === 'PUBLISH' && (
        <UnpublishModal
          isOpen={unpublishModalOpen}
          onClose={() => setUnpublishModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          itemType="Member"
          isLoading={updateStatusMutation.isPending}
        />
      )}
    </div>
  );
};

export default Members;
